import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { WarningIcon } from '@/app/common/icons/support'
import { AppRoutePaths } from '@/routing/route_paths'
import { useNavigate } from 'react-router-dom'
import MarketplaceUI from '../components/MarketplaceUI'
import { TABLE_FILTERS, MARKETPLACE_AVAILABILITY_FILTERS } from '../constants'
import { useMarketplacePreviewData } from './hooks/useMarketplacePreviewData'
import {
  MarketplaceAffiliation,
  MarketplaceStore,
} from '@/queries/marketplace/schema'
import { MarketplaceAccessPageProps } from '@/apis/marketplace-preview/types'

export function MarketplacePreviewPage({
  mode = 'preview',
}: MarketplaceAccessPageProps) {
  const navigate = useNavigate()

  const marketplacePaths =
    mode === 'published'
      ? AppRoutePaths.marketplace_view
      : AppRoutePaths.marketplace_preview

  const {
    itemSearch,
    setItemSearch,
    currentPage,
    perPage,
    setStoreSearch,
    storeSearch,
    affiliationSearch,
    setAffiliationSearch,
    currentView,

    setCurrentPage,
    setPerPage,
    selectedAvailability,
    handleFilterOnChange,
    storesQuery,
    itemsQuery,
    affiliationsQuery,
    handleToggleView,
    handleAvailabilityFilterOnChange,
  } = useMarketplacePreviewData(mode)

  const handleShopClick = (item: MarketplaceItem) => {
    navigate(marketplacePaths.details.replace(':itemId', item.id.toString()), {
      state: { item },
    })
  }

  function handleProviderClick(providerId: number, store?: MarketplaceStore) {
    navigate(
      marketplacePaths.provider_details.replace(
        ':providerId',
        providerId.toString()
      ),
      { state: { store } }
    )
  }

  const handleStoreClick = (store: MarketplaceStore) => {
    handleProviderClick(store.id, store)
  }

  const handleAffiliationClick = (affiliation: MarketplaceAffiliation) => {
    navigate(
      marketplacePaths.affiliation_details.replace(
        ':affiliationId',
        affiliation.id.toString()
      )
    )
  }

  const storesPage = storesQuery.data
  const allStores = storesPage?.data ?? []

  // The stores endpoint takes no availability parameter, so the filter is
  // applied here. In the store scoped preview it decides the whole view, since
  // the single store owns every item and affiliation on it.
  const previewStore = mode === 'preview' ? allStores[0] : undefined

  const matchesSelectedAvailability = (store: MarketplaceStore) =>
    selectedAvailability === undefined ||
    store.accepting_new_clients === selectedAvailability

  const previewStoreMatchesAvailability =
    !previewStore || matchesSelectedAvailability(previewStore)

  const stores = allStores.filter(matchesSelectedAvailability)

  const items = previewStoreMatchesAvailability
    ? (itemsQuery.data?.data ?? [])
    : []

  const affiliations = previewStoreMatchesAvailability
    ? (affiliationsQuery.data?.data ?? [])
    : []

  const search =
    currentView === 'stores'
      ? storeSearch
      : currentView === 'affiliations'
        ? affiliationSearch
        : itemSearch

  const setSearch =
    currentView === 'stores'
      ? setStoreSearch
      : currentView === 'affiliations'
        ? setAffiliationSearch
        : setItemSearch

  const totalRecords =
    currentView === 'stores'
      ? // The availability filter is applied client side, so once it is set the
        // count can only reflect the page that is currently loaded.
        selectedAvailability === undefined
        ? (storesPage?.total ?? 0)
        : stores.length
      : currentView === 'affiliations'
        ? previewStoreMatchesAvailability
          ? (affiliationsQuery.data?.total ?? 0)
          : 0
        : previewStoreMatchesAvailability
          ? (itemsQuery.data?.total ?? 0)
          : 0

  return (
    <>
      <div className="">
        <MarketplaceUI
          handleStoreClick={handleStoreClick}
          items={items}
          stores={stores}
          affiliations={affiliations}
          totalRecords={totalRecords}
          search={search}
          currentView={currentView}
          currentPage={currentPage}
          perPage={perPage}
          filters={TABLE_FILTERS}
          availabilityFilters={MARKETPLACE_AVAILABILITY_FILTERS}
          onSearchChange={setSearch}
          onViewChange={handleToggleView}
          onFilterChange={handleFilterOnChange}
          onAvailabilityFilterChange={handleAvailabilityFilterOnChange}
          onItemClick={handleShopClick}
          onAffiliationClick={handleAffiliationClick}
          onProviderClick={handleProviderClick}
          onPageChange={setCurrentPage}
          onPerPageChange={setPerPage}
          // Marketplace preview should not have a cart.
          // So we hide the cart and do nothing.
          hideCart={() => {}}
          showCart={false}
          showAffiliationsView
        />
        {mode === 'preview' && previewStore?.marketplace === 0 && (
          <Card className="mt-4">
            <CardHeader className="flex items-center gap-2 font-medium">
              <WarningIcon className="text-yellow-500 text-lg" />
              <h1>Your marketplace is turned off</h1>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-700">
                <p>
                  Your marketplace is currently disabled and not visible to the
                  public. You can still preview your items here, but customers
                  won't be able to see or purchase from your store.
                </p>
                <p className="mt-3">
                  <strong>To make your marketplace public:</strong> Go to
                  marketplace settings and enable "Display Store in
                  Marketplace".
                </p>
                <p className="mt-3 text-sm text-gray-500">
                  Need help? Contact your administrator.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}
